import React, { useEffect, useState } from 'react';
import './Brand24ConnectDialog.scss';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { Brand24Service, Brand24Mode } from '../../services/integrations/Brand24Service';

interface Brand24ConnectDialogProps {
  visible: boolean;
  onHide: () => void;
  onConnected?: () => void;
  defaultMode?: Brand24Mode;
}

// The built-in (shared) API key option is temporarily blocked — only OWN_API_KEY can be added for now.
// Flip to true to bring back the DEFAULT (built-in key) option.
const DEFAULT_MODE_ENABLED: boolean = false;

const Brand24ConnectDialog: React.FC<Brand24ConnectDialogProps> = ({
  visible,
  onHide,
  onConnected,
  defaultMode = 'DEFAULT',
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Brand24Mode>(
    DEFAULT_MODE_ENABLED ? defaultMode : 'OWN_API_KEY'
  );
  const [apiKey, setApiKey] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setMode(DEFAULT_MODE_ENABLED ? defaultMode : 'OWN_API_KEY');
      setApiKey('');
      setAccountId('');
    }
  }, [visible, defaultMode]);

  // OWN_API_KEY needs both the key and the Brand24 account id (the latter to list the user's projects).
  const ownFieldsMissing =
    mode === 'OWN_API_KEY' && (apiKey.trim().length === 0 || accountId.trim().length === 0);

  const handleConnect = async () => {
    setSaving(true);
    try {
      await Brand24Service.connect(
        mode,
        mode === 'OWN_API_KEY' ? apiKey.trim() : undefined,
        mode === 'OWN_API_KEY' ? Number(accountId.trim()) : undefined
      );
      onConnected?.();
      onHide();
    } catch (error) {
      console.error('Error connecting to Brand24:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header={t('brand24ConfigTitle')}
      visible={visible}
      onHide={onHide}
      className="dialog-box brand24-dialog"
      style={{ width: '480px' }}
      footer={
        <button
          className="btn btn--sm"
          type="button"
          disabled={saving || ownFieldsMissing}
          onClick={handleConnect}
        >
          {t('connect')}
        </button>
      }
    >
      <div className="brand24-dialog__options">
        {DEFAULT_MODE_ENABLED && (
          <div
            className={`brand24-dialog__option ${
              mode === 'DEFAULT' ? 'brand24-dialog__option--selected' : ''
            }`}
          >
            <div
              className="brand24-dialog__option-head"
              role="button"
              tabIndex={0}
              onClick={() => setMode('DEFAULT')}
            >
              <div>
                <div className="brand24-dialog__option-title">{t('brand24ModeDefault')}</div>
                <div className="brand24-dialog__option-desc">{t('brand24ModeDefaultDesc')}</div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`brand24-dialog__option ${
            mode === 'OWN_API_KEY' ? 'brand24-dialog__option--selected' : ''
          }`}
        >
          <div
            className="brand24-dialog__option-head"
            role="button"
            tabIndex={0}
            onClick={() => setMode('OWN_API_KEY')}
          >
            <div>
              <div className="brand24-dialog__option-title">{t('brand24ModeOwnKey')}</div>
              <div className="brand24-dialog__option-desc">{t('brand24ModeOwnKeyDesc')}</div>
            </div>
          </div>

          {mode === 'OWN_API_KEY' && (
            <div className="brand24-dialog__form">
              <div className="brand24-dialog__field">
                <label className="brand24-dialog__label" htmlFor="brand24-api-key">
                  {t('brand24ApiKeyLabel')}
                </label>
                <InputText
                  id="brand24-api-key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={t('brand24ApiKeyPlaceholder')}
                  className="brand24-dialog__input"
                />
              </div>

              <div className="brand24-dialog__field">
                <label className="brand24-dialog__label" htmlFor="brand24-account-id">
                  {t('brand24AccountIdLabel')}
                </label>
                <InputText
                  id="brand24-account-id"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={t('brand24AccountIdPlaceholder')}
                  className="brand24-dialog__input"
                  inputMode="numeric"
                />
                <div className="brand24-dialog__hint">{t('brand24AccountIdHint')}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default Brand24ConnectDialog;
